import logo from "@/assets/img/FacilityAI_Logo.svg";
import { useLogin } from "@/hooks/auth-queries";
import { updateUserState } from "@/hooks/user-queries";
import { AuthProviders, auth } from "@/services/auth-service";
import { trialSignup } from "@/services/user-service";
import {
	BankOutlined,
	LockOutlined,
	MailOutlined,
	PhoneOutlined,
	TeamOutlined,
	UserOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Divider,
	Form,
	Input,
	Layout,
	Select,
	Typography,
	message,
} from "antd";
import { useCallback, useReducer } from "react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";

const { Content, Footer } = Layout;
const { Text } = Typography;

// Define the state structure
interface AuthState {
	mode: "login" | "reset" | "trial";
	email: string;
	password: string;
	name: string;
	org: string;
	phone: string;
	companySize: string;
	isLoading: boolean;
	isResetting: boolean;
	isSigningUp: boolean;
}

// Define all possible actions
type AuthModeType = "login" | "reset" | "trial";

type AuthAction =
	| { type: "SET_MODE"; payload: AuthModeType }
	| { type: "SET_EMAIL"; payload: string }
	| { type: "SET_PASSWORD"; payload: string }
	| { type: "SET_NAME"; payload: string }
	| { type: "SET_ORG"; payload: string }
	| { type: "SET_PHONE"; payload: string }
	| { type: "SET_COMPANY_SIZE"; payload: string }
	| { type: "SET_LOADING"; payload: boolean }
	| { type: "SET_RESETTING"; payload: boolean }
	| { type: "SET_SIGNING_UP"; payload: boolean };

const initialState: AuthState = {
	mode: "login",
	email: "",
	password: "",
	name: "",
	org: "",
	phone: "",
	companySize: "",
	isLoading: false,
	isResetting: false,
	isSigningUp: false,
};

function reducer(state: AuthState, action: AuthAction): AuthState {
	switch (action.type) {
		case "SET_MODE":
			return {
				...state,
				mode: action.payload,
				name: "",
				org: "",
				password: "",
				email: "",
				phone: "",
				companySize: "",
				isLoading: false,
				isResetting: false,
				isSigningUp: false,
			};
		case "SET_EMAIL":
			return { ...state, email: action.payload };
		case "SET_PASSWORD":
			return { ...state, password: action.payload };
		case "SET_NAME":
			return { ...state, name: action.payload };
		case "SET_ORG":
			return { ...state, org: action.payload };
		case "SET_PHONE":
			return { ...state, phone: action.payload };
		case "SET_COMPANY_SIZE":
			return { ...state, companySize: action.payload };
		case "SET_LOADING":
			return { ...state, isLoading: action.payload };
		case "SET_RESETTING":
			return { ...state, isResetting: action.payload };
		case "SET_SIGNING_UP":
			return { ...state, isSigningUp: action.payload };
		default:
			return state;
	}
}

export const Login: FC = () => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const navigate = useNavigate();
	const { updateUser } = updateUserState();
	const login = useLogin();

	const submitLogin = useCallback(async () => {
		if (!state.email || !state.password) {
			message.warning("Please enter your email and password");
			return;
		}

		dispatch({ type: "SET_LOADING", payload: true });

		try {
			const result = await login(state.email, state.password);
			if (result?.success) {
				message.success("Login Successful");
				navigate("/");
			} else {
				message.error(result?.error || "Login Failed");
				dispatch({ type: "SET_LOADING", payload: false });
			}
		} catch (error) {
			message.error("An unexpected error occurred");
			dispatch({ type: "SET_LOADING", payload: false });
		}
	}, [login, state.email, state.password, navigate]);

	const submitResetPassword = useCallback(async () => {
		if (!state.email) {
			message.warning("Please enter your email");
			return;
		}

		dispatch({ type: "SET_RESETTING", payload: true });

		try {
			// Configure action code settings for password reset
			const actionCodeSettings = {
				url: window.location.origin + "/login",
				handleCodeInApp: false,
			};

			// Use the Firebase sendPasswordResetEmail function with action code settings
			await AuthProviders.sendPasswordResetEmail(
				auth,
				state.email,
				actionCodeSettings,
			);
			message.success(
				"Password reset email sent! Please check your inbox and spam folder.",
			);
			dispatch({ type: "SET_MODE", payload: "login" });
		} catch (error: any) {
			console.error("Password reset error:", error);

			// Handle specific Firebase Auth errors
			let errorMessage = "Password reset failed. Please try again.";

			if (error.code === "auth/user-not-found") {
				errorMessage = "No account found with this email address.";
			} else if (error.code === "auth/invalid-email") {
				errorMessage = "Invalid email address.";
			} else if (error.code === "auth/too-many-requests") {
				errorMessage = "Too many requests. Please try again later.";
			} else if (error.code === "auth/missing-continue-uri") {
				errorMessage = "Configuration error. Please contact support.";
			} else if (error.code === "auth/invalid-continue-uri") {
				errorMessage = "Configuration error. Please contact support.";
			} else if (error.code === "auth/unauthorized-continue-uri") {
				errorMessage = "Configuration error. Please contact support.";
			} else if (error.message) {
				errorMessage = error.message;
			}

			message.error(errorMessage);
		} finally {
			dispatch({ type: "SET_RESETTING", payload: false });
		}
	}, [state.email]);

	const submitTrialSignup = useCallback(async () => {
		if (
			!state.email ||
			!state.password ||
			!state.name ||
			!state.org ||
			!state.phone ||
			!state.companySize
		) {
			message.warning("Please fill out all required fields");
			return;
		}

		dispatch({ type: "SET_SIGNING_UP", payload: true });

		try {
			const user = await trialSignup(
				state.name,
				state.email,
				state.password,
				state.org,
				state.phone,
				state.companySize,
			);
			if (user) {
				// Don't log in automatically - show success message and stay on login page
				message.success(
					"Trial account created successfully! Please log in to get started.",
				);
				// Reset form and switch back to login mode
				dispatch({ type: "SET_MODE", payload: "login" });
			}
		} catch (error) {
			message.error("Trial Account Creation Failed");
			dispatch({ type: "SET_SIGNING_UP", payload: false });
		}
	}, [
		state.name,
		state.email,
		state.password,
		state.org,
		state.phone,
		state.companySize,
	]);

	const renderLoginForm = () => (
		<>
			<Form layout="vertical" onFinish={submitLogin}>
				<Form.Item
					label="Email"
					name="email"
					rules={[{ required: true, message: "Please enter your email" }]}
				>
					<Input
						placeholder="Email"
						prefix={<MailOutlined />}
						value={state.email}
						onChange={(e) =>
							dispatch({ type: "SET_EMAIL", payload: e.target.value })
						}
					/>
				</Form.Item>
				<Form.Item
					label="Password"
					name="password"
					rules={[{ required: true, message: "Please enter your password" }]}
				>
					<Input.Password
						placeholder="Password"
						prefix={<LockOutlined />}
						value={state.password}
						onChange={(e) =>
							dispatch({ type: "SET_PASSWORD", payload: e.target.value })
						}
					/>
				</Form.Item>
				<Form.Item style={{ marginTop: "4vh" }}>
					<Button
						type="primary"
						htmlType="submit"
						block
						loading={state.isLoading}
						disabled={state.isLoading}
					>
						Log In
					</Button>
				</Form.Item>
			</Form>
			<Divider>OR</Divider>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					marginBottom: "1rem",
				}}
			>
				<Button
					type="link"
					onClick={() => dispatch({ type: "SET_MODE", payload: "reset" })}
					disabled={state.isLoading}
				>
					Reset Password
				</Button>
			</div>
			<Button
				type="default"
				block
				style={{
					backgroundColor: "#52c41a",
					borderColor: "#52c41a",
					color: "white",
					fontWeight: "bold",
				}}
				onClick={() => dispatch({ type: "SET_MODE", payload: "trial" })}
				disabled={state.isLoading}
			>
				Start Free Trial
			</Button>
		</>
	);

	const renderResetForm = () => (
		<>
			<Text
				style={{
					display: "block",
					marginBottom: "2vh",
					textAlign: "center",
					fontSize: "1.5vh",
				}}
			>
				Enter your email address and we'll send you a link to reset your
				password.
			</Text>
			<Form layout="vertical" onFinish={submitResetPassword}>
				<Form.Item
					label="Email"
					name="resetEmail"
					rules={[
						{
							required: true,
							type: "email",
							message: "Please enter a valid email",
						},
					]}
				>
					<Input
						placeholder="Email"
						prefix={<MailOutlined />}
						value={state.email}
						onChange={(e) =>
							dispatch({ type: "SET_EMAIL", payload: e.target.value })
						}
					/>
				</Form.Item>
				<Form.Item style={{ marginTop: "4vh" }}>
					<Button
						type="primary"
						htmlType="submit"
						block
						loading={state.isResetting}
						disabled={state.isResetting}
					>
						Submit
					</Button>
				</Form.Item>
			</Form>
			<Divider>OR</Divider>
			<Button
				type="default"
				block
				onClick={() => dispatch({ type: "SET_MODE", payload: "login" })}
				disabled={state.isResetting}
			>
				Go Back
			</Button>
		</>
	);

	const renderTrialForm = () => (
		<>
			<div style={{ textAlign: "center", marginBottom: "2rem" }}>
				<Text
					style={{ fontSize: "1.2rem", color: "#52c41a", fontWeight: "bold" }}
				>
					Start Your Free 7-Day Trial
				</Text>
				<br />
				<Text type="secondary" style={{ fontSize: "0.9rem" }}>
					No credit card required • Full access to all features
				</Text>
			</div>
			<Form layout="vertical" onFinish={submitTrialSignup}>
				<Form.Item
					label="Full Name"
					name="name"
					rules={[{ required: true, message: "Please enter your name" }]}
				>
					<Input
						placeholder="Full Name"
						prefix={<UserOutlined />}
						value={state.name}
						onChange={(e) =>
							dispatch({ type: "SET_NAME", payload: e.target.value })
						}
					/>
				</Form.Item>
				<Form.Item
					label="Email"
					name="email"
					rules={[
						{
							required: true,
							type: "email",
							message: "Please enter a valid email",
						},
					]}
				>
					<Input
						placeholder="Email"
						prefix={<MailOutlined />}
						value={state.email}
						onChange={(e) =>
							dispatch({ type: "SET_EMAIL", payload: e.target.value })
						}
					/>
				</Form.Item>
				<Form.Item
					label="Password"
					name="password"
					rules={[
						{ required: true, message: "Please enter a password" },
						{ min: 6, message: "Password must be at least 6 characters" },
					]}
				>
					<Input.Password
						placeholder="Password"
						prefix={<LockOutlined />}
						value={state.password}
						onChange={(e) =>
							dispatch({ type: "SET_PASSWORD", payload: e.target.value })
						}
					/>
				</Form.Item>
				<Form.Item
					label="Organization"
					name="organization"
					rules={[
						{ required: true, message: "Please enter your organization" },
					]}
				>
					<Input
						placeholder="Organization Name"
						prefix={<BankOutlined />}
						value={state.org}
						onChange={(e) =>
							dispatch({ type: "SET_ORG", payload: e.target.value })
						}
					/>
				</Form.Item>
				<Form.Item
					label="Company Size"
					name="companySize"
					rules={[
						{ required: true, message: "Please select your company size" },
					]}
				>
					<Select
						placeholder="Select company size"
						value={state.companySize || undefined}
						onChange={(value) =>
							dispatch({ type: "SET_COMPANY_SIZE", payload: value })
						}
						suffixIcon={<TeamOutlined />}
						options={[
							{ value: "1-10", label: "1-10 employees" },
							{ value: "11-50", label: "11-50 employees" },
							{ value: "51-200", label: "51-200 employees" },
							{ value: "201-500", label: "201-500 employees" },
							{ value: "501-1000", label: "501-1,000 employees" },
							{ value: "1000+", label: "1,000+ employees" },
						]}
					/>
				</Form.Item>
				<Form.Item
					label="Phone Number"
					name="phone"
					rules={[
						{ required: true, message: "Please enter your phone number" },
						{
							pattern: /^[\d\s\-\+\(\)]+$/,
							message: "Please enter a valid phone number",
						},
					]}
				>
					<Input
						placeholder="Phone Number"
						prefix={<PhoneOutlined />}
						value={state.phone}
						onChange={(e) =>
							dispatch({ type: "SET_PHONE", payload: e.target.value })
						}
					/>
				</Form.Item>
				<Form.Item style={{ marginTop: "3vh" }}>
					<Button
						type="primary"
						htmlType="submit"
						block
						loading={state.isSigningUp}
						disabled={state.isSigningUp}
						style={{
							backgroundColor: "#52c41a",
							borderColor: "#52c41a",
							height: "45px",
							fontSize: "1rem",
							fontWeight: "bold",
						}}
					>
						Start Free Trial
					</Button>
				</Form.Item>
			</Form>
			<div style={{ textAlign: "center", margin: "1rem 0" }}>
				<Text type="secondary" style={{ fontSize: "0.8rem" }}>
					By starting your trial, you agree to our terms of service
				</Text>
			</div>
			<Divider>Already have an account?</Divider>
			<Button
				type="default"
				block
				onClick={() => dispatch({ type: "SET_MODE", payload: "login" })}
				disabled={state.isSigningUp}
			>
				Sign In
			</Button>
		</>
	);

	return (
		<Layout style={{ minHeight: "100dvh" }}>
			<Content
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "5vh 2vw",
				}}
			>
				<Card style={{ width: "90vw", maxWidth: "50rem", padding: "4vh 2vw" }}>
					<div style={{ textAlign: "center", marginBottom: "4vh" }}>
						<img
							src={logo}
							alt="Facility AI Logo"
							style={{
								maxHeight: "20vh",
								width: "auto",
								display: "block",
								margin: "0 auto",
							}}
						/>
					</div>
					{state.mode === "login" && renderLoginForm()}
					{state.mode === "reset" && renderResetForm()}
					{state.mode === "trial" && renderTrialForm()}
				</Card>
			</Content>
			<Footer
				style={{ textAlign: "center", fontSize: "1.5vh", padding: "2vh 0" }}
			>
				&copy; {new Date().getFullYear()} FacilityAI, LLC
			</Footer>
		</Layout>
	);
};
